import { api } from '@marketplace-web/core-api/core-api-client-util'

import {
  CreateDeliveryOptionChangeRequestArgs,
  GetDeliveryOptionChangeRequestDetailsResp,
  getDeliveryTypeResp,
  UpdateDeliveryOptionChangeRequestArgs,
} from '../types/change-delivery-option'
import {
  CreateShippingDeadlineExtensionArgs,
  GetDeadlineExtensionOptionsResp,
  UpdateShippingDeadlineExtensionArgs,
} from '../types/deadline-extension-options'
import {
  DownloadPickupParcelLabelResp,
  GetPickUpParcelCodeArgs,
  PickUpParcelCodeResponse,
} from '../types/pick-up-parcel-code'
import { DownloadShippingLabelResp } from '../types/download-shipping-label'
import { GetAvailableCarriersArgs, GetAvailableCarriersResp } from '../types/available-carrier'
import {
  GetAvailablePickupPointsArgs,
  GetAvailablePickupPointsResp,
} from '../types/available-pickup-point'
import { GetCollectionDatesResp } from '../types/collection-dates'
import { GetDigitalLabelArgs, DigitalLabelResp } from '../types/digital-label'
import { GetDownloadLabelUrlResp } from '../types/download-label'
import { GetLabelOptionsRewriteResp } from '../types/label-options'
import { addNearbyDropOffPointArgsToParams } from '../transformers/add-nearby-drop-off-point-args-to-params'
import { GetNearbyDropOffPointsArgs, NearbyDropOffPointResp } from '../types/nearby-drop-off-point'
import { GetShipmentDetailsResp } from '../types/shipment-details'
import {
  ShipmentInstructionsResponse,
  GetShipmentInstructionsArgs,
} from '../types/shipment-instructions'
import { GetShippingInstructionsResp } from '../types/shipping-instructions'
import {
  GetTrackingJourneySummaryArgs,
  TrackingJourneySummaryResponse,
} from '../types/tracking-journey-summary'
import {
  CancelTransactionArgs,
  TransactionCancellationReasonsResp,
} from '../types/transaction-cancellation'
import { MarkAsShippedArgs } from '../types/mark-as-shipped'
import { OrderShippingLabelArgs } from '../types/order-shipping-label'
import { PackagingTipsVisibilityResp } from '../types/packaging-tips'

const moneyObjectHeader = {
  'X-Money-Object': 'true',
}

export const confirmCancellationAgreement = (transactionId: number) =>
  api.post(`/transactions/${transactionId}/cancellation_agreement/confirm`)

export const createDeliveryOptionChangeRequest = ({
  shipmentId,
  rootRateUuid,
  reason,
}: CreateDeliveryOptionChangeRequestArgs) =>
  api.post(`shipments/${shipmentId}/delivery_option_change_request`, {
    root_rate_uuid: rootRateUuid,
    reason,
  })

export const createShippingDeadlineExtension = (args: CreateShippingDeadlineExtensionArgs) =>
  api.post(`shipments/${args.shipmentId}/deadline_extension`, {
    deadline_extension: {
      key: args.key,
      reason: args.reason,
    },
  })

export const declineCancellationAgreement = (transactionId: number) =>
  api.post(`/transactions/${transactionId}/cancellation_agreement/decline`)

export const downloadPickupLabel = (transactionId: number) =>
  api.get<DownloadPickupParcelLabelResp>(
    `/transactions/${transactionId}/shipment/parcel_collection_code_pdf`,
  )

export const downloadShippingLabel = (transactionId: number) =>
  api.get<DownloadShippingLabelResp>(`/transactions/${transactionId}/shipment/pdf_label`)

export const getAvailableCarriers = ({
  shippingOrderId,
  selectedDeliveryType,
  shipmentId,
}: GetAvailableCarriersArgs) =>
  api.get<GetAvailableCarriersResp>(
    `shipping_orders/${shippingOrderId}/available_rates?delivery_type=${selectedDeliveryType}&shipment_id=${shipmentId}&exclude_current_rate=true`,
  )

export const getAvailablePickupPoints = ({
  shippingOrderId,
  countryCode,
  latitude,
  longitude,
  shipmentId,
  rateRootUuid,
}: GetAvailablePickupPointsArgs) =>
  api.get<GetAvailablePickupPointsResp>(
    `shipping_orders/${shippingOrderId}/nearby_pickup_points?country_code=${countryCode}&latitude=${latitude}&longitude=${longitude}&shipment_id=${shipmentId}&rate_root_uuid=${rateRootUuid}`,
  )

export const getCollectionDates = (shipmentId: number) =>
  api.get<GetCollectionDatesResp>(`shipments/${shipmentId}/collection_dates`)

export const getDeadlineExtensionOptions = (shipmentId: number) =>
  api.get<GetDeadlineExtensionOptionsResp>(`shipments/${shipmentId}/deadline_extension_options`)

export const getDeliveryOptionChangeRequestDetails = (shipmentId: number) =>
  api.get<GetDeliveryOptionChangeRequestDetailsResp>(
    `shipments/${shipmentId}/delivery_option_change_request`,
  )

export const getDeliveryType = (shipmentId: number) =>
  api.get<getDeliveryTypeResp>(`shipments/${shipmentId}`)

export const getDigitalLabel = ({ transactionId }: GetDigitalLabelArgs) =>
  api.get<DigitalLabelResp>(`/transactions/${transactionId}/shipment/digital_label`)

export const getDownloadLabelUrl = (shipmentId: number) =>
  api.get<GetDownloadLabelUrlResp>(`shipments/${shipmentId}/label_url`)

export const getLabelOptions = (shipmentId: number) =>
  api.get<GetLabelOptionsRewriteResp>(`shipments/${shipmentId}/label_options`, {
    params: {
      drop_off_rewrite: true,
    },
  })

export const getNearbyDropOffPoints = (args: GetNearbyDropOffPointsArgs) =>
  api.get<NearbyDropOffPointResp>(`/shipments/${args.shipmentId}/nearby_drop_off_points`, {
    params: addNearbyDropOffPointArgsToParams(args),
  })

export const getPickUpParcelCode = ({ transactionId }: GetPickUpParcelCodeArgs) =>
  api.get<PickUpParcelCodeResponse>(`transactions/${transactionId}/shipment/parcel_collection_code`)

export const getShipmentDetails = (shipmentId: number) =>
  api.get<GetShipmentDetailsResp>(`/shipments/${shipmentId}`)

export const getShipmentInstructions = ({ transactionId }: GetShipmentInstructionsArgs) =>
  api.get<ShipmentInstructionsResponse>(`transactions/${transactionId}/shipment_instructions`, {
    headers: moneyObjectHeader,
  })

export const getShippingInstructions = (transactionId: number) =>
  api.get<GetShippingInstructionsResp>(`/transactions/${transactionId}/shipping_instructions`)

export const getTrackingJourneySummary = ({ transactionId }: GetTrackingJourneySummaryArgs) =>
  api.get<TrackingJourneySummaryResponse>(`transactions/${transactionId}/shipment/journey_summary`)

export const getTransactionCancellationReasons = (id: number) =>
  api.get<TransactionCancellationReasonsResp>(`/transactions/${id}/cancellation_reasons`)

export const markAsShipped = ({ transactionId, proofPhotoUuid }: MarkAsShippedArgs) =>
  api.put(`/transactions/${transactionId}/shipment/mark_as_shipped`, {
    proof_photo_uuid: proofPhotoUuid,
  })

export const orderShippingLabel = ({
  sellerAddressId,
  transactionId,
  pickUpDate,
  digitalLabelType,
  dropOffTypeKey,
  selectedDropOffPointType,
  reusablePackageSelected,
  labelType,
}: OrderShippingLabelArgs) =>
  api.put(`transactions/${transactionId}/shipment/order`, {
    seller_address_id: sellerAddressId,
    pick_up_date: pickUpDate,
    digital_label_type: digitalLabelType,
    drop_off_type_key: dropOffTypeKey,
    drop_off_type: selectedDropOffPointType,
    reusable_package_selected: reusablePackageSelected,
    label_type: labelType,
  })

export const partiesHaveMet = (transactionId: number) =>
  api.put(`/transactions/${transactionId}/shipment/parties_have_met`)

export const updateDeliveryOptionChangeRequest = (args: UpdateDeliveryOptionChangeRequestArgs) =>
  api.patch(`/shipments/${args.shipmentId}/delivery_option_change_request`, {
    status: args.status,
    pickup_point_code: args.pickupPointCode,
  })

export const updateShippingDeadlineExtension = (args: UpdateShippingDeadlineExtensionArgs) =>
  api.patch(`/shipments/${args.shipmentId}/deadline_extension_status`, {
    status: args.status,
  })

export const markAsDeliveredToSender = (transactionId: number) =>
  api.put(`/transactions/${transactionId}/shipment/mark_as_return_to_sender_completed`)

export const markAsDelivered = (shipmentId: number) =>
  api.put(`/shipments/${shipmentId}/mark_as_delivered`)

export const completeTransaction = (id: string | number) =>
  api.put(`/transactions/${id}/complete`, undefined, { headers: moneyObjectHeader })

export const cancelTransaction = ({ id, reasonId, userExplanation }: CancelTransactionArgs) =>
  api.put(`/transactions/${id}/cancel`, {
    reason: {
      id: reasonId,
      user_explanation: userExplanation,
    },
  })

export const getPackagingTipsVisibility = (shipmentId: number) =>
  api.get<PackagingTipsVisibilityResp>(`/shipments/${shipmentId}/packaging_tips/visibility`)

export const markPackagingTipsVisibilityShown = () => api.post('packaging_tips/mark_as_shown')
